import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { STATUS_ORDER, STATUS, PRIORITIES } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  Loader2, Wand2, AlertTriangle, ArrowLeft, X, ChevronDown, ChevronUp,
  ExternalLink, Sparkles, Users,
} from "lucide-react";

const EXAMPLE = `Name: Friseur Mustermann
Branche: Friseur
Bezirk: Pankow
Telefon: 030 12345678
E-Mail: info@mustermann.de
Website: https://mustermann.de
Instagram: https://instagram.com/mustermann
Ansprechpartner: Herr Mustermann
Status: Neu
Priorität: Hoch
Problem/Chance: Website wirkt veraltet und hat keinen klaren Termin-Button.
Individueller Gesprächseinstieg: Mir ist aufgefallen, dass Ihre Website mobil etwas schwer zu bedienen ist.
Notizen: Gute Google-Bewertungen, lokaler Einzelbetrieb.

---

Name: Restaurant Bella
Branche: Gastronomie
Telefon: 030 87654321
E-Mail: kontakt@bella.de`;

const MORE_FIELDS = [
  { key: "industry", label: "Branche" },
  { key: "district", label: "Bezirk" },
  { key: "website", label: "Website" },
  { key: "instagram", label: "Instagram" },
];

function buildCreatePayload(f) {
  return {
    company: f.company, industry: f.industry, website: f.website,
    contact_person: f.contact_person, phone: f.phone, email: f.email,
    address: f.address, district: f.district, instagram: f.instagram,
    priority: f.priority || "mittel", problem: f.problem, pitch: f.pitch,
    notes: f.notes, potential_value: parseFloat(f.potential_value) || 0,
    status: f.status || "neu",
  };
}

function buildUpdatePayload(f) {
  const p = {};
  ["company", "industry", "website", "contact_person", "phone", "email",
   "address", "district", "instagram", "problem", "pitch", "notes"].forEach((k) => {
    if (f[k] && String(f[k]).trim()) p[k] = f[k];
  });
  if (f.priority) p.priority = f.priority;
  const pv = parseFloat(f.potential_value);
  if (pv > 0) p.potential_value = pv;
  return p; // status wird bewusst NICHT überschrieben
}

function ImportItemCard({ item, index, single, onChange, onRemove, onOpenExisting }) {
  const [expanded, setExpanded] = useState(single);
  const { fields, duplicates, action } = item;
  const set = (k, v) => onChange(index, { ...item, fields: { ...fields, [k]: v } });
  const setAction = (a) => onChange(index, { ...item, action: a });

  const invalidCompany = !fields.company?.trim();
  const invalidCreate = action === "create" && !fields.phone?.trim() && !fields.email?.trim();

  return (
    <div data-testid={`import-item-${index}`}
      className={cn("rounded-xl border bg-card p-4", action === "skip" ? "opacity-50 border-border" : "border-border")}>
      <div className="flex items-center gap-2">
        <Input data-testid={`import-item-company-${index}`} value={fields.company || ""}
          onChange={(e) => set("company", e.target.value)} placeholder="Firmenname *"
          className={cn("font-semibold", invalidCompany && "border-rose-500/60")} />
        <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0"
          onClick={() => setExpanded((v) => !v)} data-testid={`import-item-toggle-${index}`}>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
        {!single && (
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0 text-rose-400"
            onClick={() => onRemove(index)} data-testid={`import-item-remove-${index}`}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {duplicates.length > 0 && (
        <div data-testid={`import-duplicate-warning-${index}`}
          className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" /> Mögliches Duplikat
          </div>
          <div className="mt-1 text-xs text-amber-200/90 flex items-center flex-wrap gap-x-2">
            <button className="underline hover:text-amber-100 inline-flex items-center gap-1"
              onClick={() => onOpenExisting(duplicates[0].id)} data-testid={`import-open-existing-${index}`}>
              {duplicates[0].company} <ExternalLink className="h-3 w-3" />
            </button>
            <span>— {duplicates[0].matched_on.join(", ")}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[
              { v: "create", l: "Neu anlegen" },
              { v: "update", l: "Bestehenden aktualisieren" },
              { v: "skip", l: "Überspringen" },
            ].map((o) => (
              <button key={o.v} onClick={() => setAction(o.v)}
                data-testid={`import-action-${o.v}-${index}`}
                className={cn("rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                  action === o.v ? "border-amber-400/60 bg-amber-400/20 text-amber-100" : "border-amber-500/20 text-amber-200/70 hover:bg-amber-500/10")}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
      )}

      {invalidCreate && (
        <p className="mt-2 text-xs text-rose-300">Für „Neu anlegen" wird mindestens Telefon oder E-Mail benötigt.</p>
      )}

      {expanded && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Ansprechpartner</Label><Input value={fields.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} /></div>
          <div><Label>Telefon</Label><Input value={fields.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          <div><Label>E-Mail</Label><Input value={fields.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
          {MORE_FIELDS.map((mf) => (
            <div key={mf.key}><Label>{mf.label}</Label><Input value={fields[mf.key] || ""} onChange={(e) => set(mf.key, e.target.value)} /></div>
          ))}
          <div>
            <Label>Status</Label>
            <Select value={fields.status || "neu"} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priorität</Label>
            <Select value={fields.priority || "mittel"} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Problem / Chance</Label><Textarea rows={2} value={fields.problem || ""} onChange={(e) => set("problem", e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Gesprächseinstieg</Label><Textarea rows={2} value={fields.pitch || ""} onChange={(e) => set("pitch", e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Notizen</Label><Textarea rows={2} value={fields.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
      )}
    </div>
  );
}

export function ImportLeadDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState("input");
  const [text, setText] = useState("");
  const [useAi, setUseAi] = useState(false);
  const [usedAi, setUsedAi] = useState(false);
  const [items, setItems] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);

  const reset = () => {
    setStep("input"); setText(""); setUseAi(false); setUsedAi(false); setItems([]);
  };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const analyze = async () => {
    if (!text.trim()) { toast.error("Bitte zuerst einen Text einfügen"); return; }
    setAnalyzing(true);
    try {
      const res = await api.post("/leads/parse-import", { text, use_ai: useAi });
      const leads = res.data.leads || [];
      if (leads.length === 0) {
        toast.error("Keine Leads erkannt. Versuche die KI-Extraktion.");
        return;
      }
      setUsedAi(res.data.used_ai);
      if (res.data.ai_error) toast.warning("KI nicht verfügbar – Text wurde klassisch analysiert.");
      setItems(leads.map((l) => ({
        fields: l.fields,
        duplicates: l.duplicates || [],
        action: (l.duplicates && l.duplicates.length > 0) ? "update" : "create",
        targetId: l.duplicates?.[0]?.id || null,
      })));
      setStep("preview");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Analyse fehlgeschlagen");
    } finally {
      setAnalyzing(false);
    }
  };

  const updateItem = (i, next) => setItems((arr) => arr.map((it, idx) => (idx === i ? next : it)));
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const openExisting = (id) => { close(false); navigate(`/leads/${id}`); };

  const doImport = async () => {
    const active = items.filter((i) => i.action !== "skip");
    if (active.length === 0) { toast.error("Keine Leads zum Importieren ausgewählt"); return; }
    for (const it of active) {
      if (!it.fields.company?.trim()) { toast.error("Ein Lead hat keinen Firmennamen"); return; }
      if (it.action === "create" && !it.fields.phone?.trim() && !it.fields.email?.trim()) {
        toast.error(`„${it.fields.company}": Telefon oder E-Mail erforderlich`); return;
      }
    }
    setImporting(true);
    try {
      const results = await Promise.allSettled(active.map((it) =>
        it.action === "update" && it.targetId
          ? api.put(`/leads/${it.targetId}`, buildUpdatePayload(it.fields))
          : api.post("/leads", buildCreatePayload(it.fields))
      ));
      const ok = results.filter((r) => r.status === "fulfilled");
      const failed = results.length - ok.length;
      const created = active.filter((it, idx) => it.action !== "update" && results[idx].status === "fulfilled");
      qc.invalidateQueries();
      if (ok.length > 0) toast.success(`${ok.length} Lead(s) importiert${failed ? `, ${failed} fehlgeschlagen` : ""}`);
      if (failed && ok.length === 0) { toast.error("Import fehlgeschlagen"); setImporting(false); return; }
      close(false);
      if (created.length === 1) {
        const idx = active.findIndex((it, i) => it.action !== "update" && results[i].status === "fulfilled");
        const rid = idx >= 0 ? results[idx].value?.data?.id : null;
        if (rid) navigate(`/leads/${rid}`);
      }
    } catch (err) {
      toast.error("Import fehlgeschlagen");
    } finally {
      setImporting(false);
    }
  };

  const activeCount = items.filter((i) => i.action !== "skip").length;
  const single = items.length === 1;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="import-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Lead(s) aus Text importieren
            {step === "preview" && usedAi && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary border border-primary/30 px-2 py-0.5 text-xs">
                <Sparkles className="h-3 w-3" /> KI
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {step === "input"
              ? "Einen oder mehrere Lead-Texte einfügen (mit --- trennen). Felder werden automatisch erkannt."
              : `${items.length} Lead(s) erkannt – prüfen, anpassen und importieren.`}
          </DialogDescription>
        </DialogHeader>

        {step === "input" ? (
          <div className="space-y-4">
            <Textarea data-testid="import-textarea" value={text} onChange={(e) => setText(e.target.value)}
              rows={12} placeholder={EXAMPLE} className="font-mono text-xs" />
            <div className="flex items-center justify-between rounded-lg border border-border bg-card p-3">
              <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-primary" />
                <div>
                  <div className="text-sm font-medium">KI-Extraktion</div>
                  <div className="text-xs text-muted-foreground">Für unstrukturierten Fließtext ohne Labels</div>
                </div>
              </div>
              <Switch checked={useAi} onCheckedChange={setUseAi} data-testid="import-ai-switch" />
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" size="sm" onClick={() => setText(EXAMPLE)} data-testid="import-example-btn">
                Beispiel einfügen
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => close(false)}>Abbrechen</Button>
                <Button onClick={analyze} disabled={analyzing} data-testid="import-analyze-btn">
                  {analyzing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
                  Analysieren
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, i) => (
              <ImportItemCard key={i} item={item} index={i} single={single}
                onChange={updateItem} onRemove={removeItem} onOpenExisting={openExisting} />
            ))}

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
              <Button variant="ghost" onClick={() => setStep("input")} className="sm:mr-auto" data-testid="import-back-btn">
                <ArrowLeft className="mr-2 h-4 w-4" /> Zurück
              </Button>
              <Button variant="ghost" onClick={() => close(false)}>Abbrechen</Button>
              <Button onClick={doImport} disabled={importing || activeCount === 0} data-testid="import-create-btn">
                {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Users className="mr-2 h-4 w-4" />}
                {activeCount > 1 ? `${activeCount} Leads importieren` : "Lead erstellen"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
